import {useCallback, useMemo} from 'react'

import {cellHasUnloadedItems} from '../cells/cell-has-unloaded-items'
import {itemPagingPosition} from '../focus/positions'
import type {BoardFocusPosition} from '../focus/types'
import {composeCellKey} from '../provider/cell-key'
import {useBoardConfig, useBoardDataContext, useBoardPaging} from '../provider/contexts'
import type {CellAddress} from '../types'
import type {PagingSentinelProps} from './use-paging-sentinel'
import {usePagingStop} from './use-paging-stop'

export type UseBoardItemPagingResult = {
  /**
   * This cell has a further page. Mount the sentinel when it is true and nothing at all
   * when it is false, so a cell holding everything it will ever hold costs no element
   * and no observer.
   */
  hasMore: boolean
  /**
   * A page for **this** cell is in flight. It is read from the cell the address names,
   * so a neighbour paging leaves this one alone.
   */
  isLoading: boolean
  /** The caller's name for this cell's paging control, already given both its labels. */
  label: string
  sentinelProps: PagingSentinelProps
}

/**
 * More cards in one cell: the first of the board's three paging directions, and the only
 * one that is addressed.
 *
 * The other two ask a question about the board. This one asks about a single
 * column-and-row pair and reports it back with both halves, so a caller can page one
 * cell without touching its neighbours, and so a board paging six cells at once makes
 * six independent requests rather than one that means six different things.
 *
 * The board fetches nothing. It reports that a cell was reached, and the next page
 * arrives as new `data`.
 */
export function useBoardItemPaging(address: CellAddress): UseBoardItemPagingResult {
  const {columnsByKey, rowsByKey, cellsByAddress} = useBoardDataContext()
  const {labels, hasRowHeaders} = useBoardConfig()
  const {onLoadMoreItems} = useBoardPaging()
  const {columnKey, rowKey} = address

  // Keyed on the two halves rather than on `address`, which callers build inline, so a
  // fresh object arrives on every render and depending on it would defeat the memo.
  const cell = useMemo(
    () => cellsByAddress.get(composeCellKey({columnKey, rowKey})),
    [cellsByAddress, columnKey, rowKey],
  )

  const column = columnsByKey.get(columnKey)
  const row = rowsByKey.get(rowKey)

  // Both halves have to resolve before this is a cell at all, the same rule
  // `useBoardCell` applies to a cell's role and its name. An address that is not on this
  // board must not be able to page, because the page it asked for could not be put
  // anywhere.
  const hasMore = column !== undefined && row !== undefined && cellHasUnloadedItems(cell)

  // Rebuilt from the two halves rather than passed through, so the address handed back
  // is the cell's own rather than whichever object this render happened to be called
  // with.
  const onReached = useCallback(() => {
    onLoadMoreItems?.({columnKey, rowKey})
  }, [onLoadMoreItems, columnKey, rowKey])

  const isLoading = cell?.isLoadingItems ?? false

  // Only built once both halves resolve. Naming a cell that is not on this board would
  // mean handing the caller's own formatter a label nothing has, and asking it to word
  // a control that is never drawn.
  const label =
    column === undefined
      ? ''
      : labels.loadMoreItems({columnLabel: column.label, rowLabel: hasRowHeaders ? row?.label : undefined})

  // Keyed on the two halves for the same reason the cell lookup above is: an address built
  // inline would rebuild the focus subscription on every render.
  const position = useMemo<BoardFocusPosition>(() => itemPagingPosition({columnKey, rowKey}), [columnKey, rowKey])

  // A caller that states a cell has more but supplies no callback still gets the
  // affordance, because having more is a fact it stated. It gets no observer, because
  // there is nobody to tell.
  const sentinelProps = usePagingStop({
    position,
    label,
    onReached,
    enabled: hasMore && onLoadMoreItems !== undefined,
    // Read from the cell the address names, so a neighbour paging never holds this
    // cell's sentinel and this cell paging never holds a neighbour's.
    isLoading,
  })

  return {hasMore, isLoading, label, sentinelProps}
}
